/**
 * @license Siftrock
 * (c) 2014-2018 Siftrock, Inc. http://siftrock.com
 * Unauthorized copying of this file, via any medium is strictly prohibited
 */

import { Component, OnInit } from '@angular/core';

import {style, animate, transition, state, trigger} from '@angular/animations';

import { ActivatedRoute } from '@angular/router';

import { BsModalService } from 'ngx-bootstrap/modal';

import { CacheService } from '../../../global/ng2-cache/services/cache.service';
import { SessionService } from '../../../services/session2.service';
import { ErrorService } from '../../../services/error.service';
import { WorkflowService } from '../../../services/workflow.service';
import { PardotService } from '../../../services/pardot.service';
import { GlobalMessageService } from '../../../services/global-message.service';
import { AlertDialogComponent } from '../../../global/alert-dialog/alert-dialog.component';
import { DriftService } from '../../../services/drift.service';

import { Constants, SiftrockField } from '../../../global/constants';

@Component({
  selector: 'app-wf-detail-pardot',
  templateUrl: './wf-detail-pardot.component.html',
  animations: [
    trigger('draftState',
      [
        state('inactive', style({
          opacity: 0,
          transform: 'scale(.9)'
        })),
        state('active', style({
          opacity: 1,
          transform: 'scale(1)'
        })),
        transition('inactive => active', animate('300ms ease-in'))
      ]
    )
  ]
})
export class WfDetailPardotComponent implements OnInit {

  loadingPage = true;
  id: string = '';
  partner: string = 'pardot';
  activeStep: string = 'type';
  help: boolean = false;
  workflow: any = {};
  wfType: string = '';
  draftSaved: string = '';
  draftState: string = 'inactive';

  // type management
  selectedTypes: any = {};
  selectedLanguages: any[] = [];
  languageList: any[] = [];
  languageDDSettings: any = {
    text: 'Select language(s)',
    unSelectAllText: 'Remove all but English'
  };

  // list state/management
  lists: any[] = [];
  currentList: string;

  // field/state management
  fields: any[] = [];
  currentSRField: SiftrockField;
  destFields: any[] = [];
  currentDestField: any;
  currentStaticField: any;
  staticValue: string = '';
  currentSelectedFields: any[] = [];
  currentSelectedMappedFields: any[] = [];

  // filter/state management
  currentIncludeFilterField: SiftrockField;
  includeFilterValue: string = '';
  currentExcludeFilterField: SiftrockField;
  excludeFilterValue: string = '';

  quality = {
    high: true,
    med: true,
    low: false
  };

  refreshState = {
    lists: 0,
    fields: 0,
  };

  constructor(private route: ActivatedRoute,
              private cacheService: CacheService,
              private sessionService: SessionService,
              private errorService: ErrorService,
              private modalService: BsModalService,
              private workflowService: WorkflowService,
              private pardotService: PardotService,
              private globalMessageService: GlobalMessageService,
              private driftService: DriftService,
              private constants: Constants) { }

  ngOnInit() {
    this.constants.emailTypes.forEach((t) => {
      this.selectedTypes[t.typeName] = false;
    });

    this.constants.languages.forEach(l => {
      if (l.supported) {
        this.languageList.push({
          id: l.name,
          itemName: l.display
        });
      }
    });

    this.languageList.push({
      id: 'other',
      itemName: 'Other'
    });

    if (!this.cacheService.get('session')) {
      this.sessionService.getSession(true).subscribe(
        (session: any) => {
          this.initView();
        }
      )
    }
    else {
      this.initView();
    }
  }

  typeChange() {
    this.workflow.trigger.type = [];
    for (var key in this.selectedTypes) {
      if (this.selectedTypes[key]) {
        this.workflow.trigger.type.push(key);
      }
    }

    var update = { 'trigger.type': this.workflow.trigger.type };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Email type' });
  }


  languageAdd(item) {
    if (this.selectedLanguages.length == this.languageList.length) {
      // 'all' languages
      this.workflow.trigger.language = ['all'];
    }
    else {
      this.workflow.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }
    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Language' });
  }

  languageRemove(item) {
    if (this.selectedLanguages.length > 0) {
      this.workflow.trigger.language = this.selectedLanguages.map(l => { return l.id });
    }
    else {
      // force at least one language
      this.selectedLanguages = [{ id: 'en', itemName: 'English'}];
      this.workflow.trigger.language = ['en'];
    }
    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Language' });
  }

  languageAddAll(items) {
    this.workflow.trigger.language = ['all'];
    var update = { 'trigger.language': ['all'] };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Language' });
  }

  languageRemoveAll(items) {
    // force at least one language
    this.selectedLanguages = [{ id: 'en', itemName: 'English'}];
    this.workflow.trigger.language = ['en'];
    var update = { 'trigger.language': this.workflow.trigger.language };
    this.saveWorkflow(update);
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Language' });
  }

  changeQuality() {
    if (!this.quality.low && !this.quality.med && !this.quality.high) {
      this.workflow.trigger.confidence = [1];
      this.modalService.show(AlertDialogComponent, Object.assign({}, {
        initialState: {
          message: `At least one contact quality must be chosen. We've automatically selected "High".`,
          confirmBtn: 'Ok'
        }
      }));

      setTimeout(() => {
        this.quality.high = true;
      }, 50);
    }
    else {
      this.workflow.trigger.confidence = [];
      if (this.quality.high) {
        this.workflow.trigger.confidence.push(1);
      }
      if (this.quality.med) {
        this.workflow.trigger.confidence.push(2);
      }
      if (this.quality.low) {
        this.workflow.trigger.confidence.push(3);
      }
    }

    this.saveWorkflow({'trigger.confidence': this.workflow.trigger.confidence});
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Contact quality' });
  }

  changeTriggerRecipient() {
    this.saveWorkflow({'trigger.recipient': this.workflow.trigger.recipient});
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Create recipient' });
  }

  refreshLists() {
    this.pardotService.clearListCache();
    this.getLists();
  }

  changeList(list) {
    this.currentList = list.value;
    this.workflow.action.list = {
      id: Math.floor(list.value),
      name: list.label
    };

    this.saveWorkflow({ 'action.list': this.workflow.action.list });
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'List' });
  }

  clearList() {
    this.currentList = '';
    this.workflow.action.list = {};
    this.saveWorkflow({ 'action.list': this.workflow.action.list });
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'List' });
  }

  refreshFields() {
    this.pardotService.clearFieldCache();
    this.getFields();
  }

  srFieldChange(field) {
    this.destFields = [];
    this.fields.forEach((f) => {
      if (f.type == field.type && !f.used) {
        this.destFields.push(f);
      }
    });
  }

  addSRFieldMapping() {
    this.workflow.action.fields.push({
      id: this.currentDestField.id,
      description: this.currentDestField.description,
      name: this.currentDestField.name,
      siftrock_description: this.currentSRField.display,
      value: this.currentSRField.fieldName
    });

    this.workflow.action.fields.sort(function (a, b) {
      if (a.description < b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.fields.forEach((f) => {
      if (f.name == this.currentDestField.name) {
        f.used = true;
      }
    });

    this.constants.siftrockFields.forEach((f) => {
      if (f.fieldName == this.currentSRField.fieldName) {
        f.used = true;
      }
    });

    this.currentSRField = null;
    this.currentDestField = null;
    this.saveWorkflow( {'action.fields': this.workflow.action.fields});
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Drift Email field map' });
  }

  removeSRFieldMapping(field) {
    this.currentSRField = null;
    this.currentDestField = null;
    this.workflow.action.fields.splice(this.workflow.action.fields.indexOf(field), 1);
    this.saveWorkflow( {'action.fields': this.workflow.action.fields});
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Drift Email field map' });
  }

  addStaticFieldMapping() {
    this.workflow.action.static_fields.push({
      id: this.currentStaticField.id,
      description: this.currentStaticField.description,
      name: this.currentStaticField.name,
      value: this.staticValue
    });

    this.fields.forEach((f) => {
      if (f.name == this.currentStaticField.name) {
        f.used = true;
      }
    });

    this.currentStaticField = null;
    this.staticValue = '';
    this.saveWorkflow( {'action.static_fields': this.workflow.action.static_fields} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Static field map' });
  }

  removeStaticFieldMapping(field) {
    this.currentStaticField = null;
    this.staticValue = '';
    this.workflow.action.static_fields.splice(this.workflow.action.static_fields.indexOf(field), 1);
    this.saveWorkflow( {'action.static_fields': this.workflow.action.static_fields} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Static field map' });
  }

  addSenderFieldMapping() {
    this.currentSelectedFields.forEach((f) => {
      this.workflow.action.recipient_fields.push(f);
      this.fields.forEach((f2) => {
        if (f2.name == f.name) {
          f2.used = true;
        }
      });
    });

    this.workflow.action.recipient_fields.sort((a,b) => {
      if (a.description > b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.currentSelectedFields = [];
    this.saveWorkflow( {'action.recipient_fields': this.workflow.action.recipient_fields} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Sender field map' });
  }

  removeSenderFieldMapping() {
    this.currentSelectedMappedFields.forEach((f) => {
      this.workflow.action.recipient_fields.splice(this.workflow.action.recipient_fields.indexOf(f), 1);
      this.fields.forEach((f2) => {
        if (f.name == f2.name) {
          f2.used = false;
        }
      });
    });

    this.workflow.action.recipient_fields.sort((a,b) => {
      if (a.description > b.description) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.currentSelectedMappedFields = [];
    this.saveWorkflow( {'action.recipient_fields': this.workflow.action.recipient_fields} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Sender field map' });
  }

  addIncludeFilter() {
    this.includeFilterValue = this.includeFilterValue.toLowerCase();
    var found = false;
    this.workflow.action.include_filters.forEach((f) => {
      if (f.field == this.currentIncludeFilterField.fieldName) {
        found = true;
        f.values.push(this.includeFilterValue);
      }
    });

    if (!found) {
      this.workflow.action.include_filters.push({
        field: this.currentIncludeFilterField.fieldName,
        display: this.currentIncludeFilterField.display,
        values: [this.includeFilterValue]
      });
    }

    this.workflow.action.include_filters.sort((a,b) => {
      if (a.display < b.display) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.includeFilterValue = '';
    this.saveWorkflow( {'action.include_filters': this.workflow.action.include_filters} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Include filter' });
  }

  removeIncludeFilter(filter, value) {
    filter.values.splice(filter.values.indexOf(value), 1);
    if (filter.values.length == 0) {
      this.workflow.action.include_filters.splice(this.workflow.action.include_filters.indexOf(filter), 1);
    }
    this.saveWorkflow( {'action.include_filters': this.workflow.action.include_filters} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Include filter' });
  }

  addExcludeFilter() {
    this.excludeFilterValue = this.excludeFilterValue.toLowerCase();
    var found = false;
    this.workflow.action.exclude_filters.forEach((f) => {
      if (f.field == this.currentExcludeFilterField.fieldName) {
        found = true;
        f.values.push(this.excludeFilterValue);
      }
    });

    if (!found) {
      this.workflow.action.exclude_filters.push({
        field: this.currentExcludeFilterField.fieldName,
        display: this.currentExcludeFilterField.display,
        values: [this.excludeFilterValue]
      });
    }

    this.workflow.action.exclude_filters.sort((a,b) => {
      if (a.display < b.display) {
        return -1;
      }
      else {
        return 1;
      }
    });

    this.excludeFilterValue = '';
    this.saveWorkflow( {'action.exclude_filters': this.workflow.action.exclude_filters} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Exclude filter' });
  }

  removeExcludeFilter(filter, value) {
    filter.values.splice(filter.values.indexOf(value), 1);
    if (filter.values.length == 0) {
      this.workflow.action.exclude_filters.splice(this.workflow.action.exclude_filters.indexOf(filter), 1);
    }
    this.saveWorkflow( {'action.exclude_filters': this.workflow.action.exclude_filters} );
    this.driftService.track('edited-workflow', { type: `${this.wfType} Contacts`, field: 'Exclude filter' });
  }

  private getLists() {
    this.refreshState.lists = 1;
    this.lists = [];
    this.pardotService.getLists().subscribe(
      (result: any) => {
        result.forEach((l) => {
          this.lists.push({
            label: l.name,
            value: l.id.toString()
          });

          if (this.workflow.action.list.id && this.workflow.action.list.id==l.id) {
            this.currentList = l.id.toString();
          }
        });
        this.refreshState.lists = 0;
        if (this.lists.length == 0) {
          this.refreshState.lists = 2;
        }

        this.lists.sort((a, b) => {
          if (a.label < b.label) {
            return -1;
          }
          else {
            return 1;
          }
        });
      },
      error => {
        this.refreshState.lists = 2;
      }
    );
  }

  private getFields() {
    this.refreshState.fields = 1;
    this.fields = [];
    this.pardotService.getFields().subscribe(
      (result: any) => {
        if (!result) {
          this.refreshState.fields = 2;
        }
        else {
          this.fields = result;
          this.refreshState.fields = 0;
          this.fields.forEach((f) => {
            f.used = false;

            // core fields are those we automatically copy or are invalid
            this.constants.coreFields.forEach((cf) => {
              if (cf.partner == this.partner && cf.name == f.name) {
                f.used = true;
              }
            });

            this.workflow.action.fields.forEach((af) => {
              if (af.name == f.name) {
                f.used = true;
              }
            });

            this.workflow.action.static_fields.forEach((sf) => {
              if (sf.name == f.name) {
                f.used = true;
              }
            });

            this.workflow.action.recipient_fields.forEach((rf) => {
              if (rf.name == f.name) {
                f.used = true;
              }
            });
          });

          this.fields.sort((a, b) => {
            if (a.description < b.description) {
              return -1;
            }
            else {
              return 1;
            }
          });
        }
      },
      error => {
        this.refreshState.fields = 2;
      }
    )
  }

  private saveWorkflow(update: any) {
    this.draftState = 'inactive';

    // Group core fields together
    this.workflow.action.fields.sort((a,b) => {
      if (a.core && !b.core) {
        return 1;
      }
      else {
        return -1;
      }
    });

    if (this.workflow._id == 'new') {
      // special case for first-time save
      this.workflowService.saveNew(this.workflow).subscribe(
        (result: any) => {
          this.workflow = result;
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
          this.driftService.track('added-workflow', { type: `${this.wfType} Contacts` });
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'wf-detail.component', 'saveWorkflow.100');
        }
      )
    }
    else {
      this.workflowService.savePartial(this.workflow._id, update).subscribe(
        (result: any) => {
          this.draftSaved = new Date().toLocaleTimeString();
          this.draftState = 'active';
        },
        error => {
          this.globalMessageService.error();
          this.errorService.log(error, 'wf-detail.component', 'saveWorkflow.200');
        }
      )
    }
  }

  private initView() {
    this.route.params.subscribe(
      params => {
        this.id = params['id'];
        if (this.id == 'new') {
          // check query string for type
          this.route.queryParams.subscribe(
            qs => {
              this.initNew(qs['type'] || 'contacts');
              this.wfType = this.workflow.trigger.data == 'contacts' ? 'Create' : 'Update';
              this.getLists();
              this.refreshFields();
              this.loadingPage = false;
            }
          );
        }
        else {
          this.workflowService.getWorkflow(this.id).subscribe(
            (result: any) => {
              this.workflow = result;
              this.workflow.trigger.type.forEach((t) => {
                this.selectedTypes[t] = true;
              });
              this.quality = {
                low: false,
                med: false,
                high: false
              };
              if (this.workflow.trigger.confidence.indexOf(1) >= 0) {
                this.quality.high = true;
              }
              if (this.workflow.trigger.confidence.indexOf(2) >= 0) {
                this.quality.med = true;
              }
              if (this.workflow.trigger.confidence.indexOf(3) >= 0) {
                this.quality.low = true;
              }
              this.wfType = this.workflow.trigger.data == 'contacts' ? 'Create' : 'Update';

              this.workflow.trigger.language.forEach(lang => {
                this.languageList.forEach(listlang => {
                  if (listlang.id == lang || lang == 'all') {
                    this.selectedLanguages.push(listlang);
                  }
                });
              });

              this.getLists();
              this.refreshFields();
              this.loadingPage = false;
            },
            error => {
              this.globalMessageService.error();
              this.errorService.log(error, 'wf-detail.component', 'ngOnInit.100');
            }
          )
        }
      }
    )
  }

  private initNew(dataType: string) {
    let defaultTypes: string[] = [];
    if (dataType=='contacts') {
      defaultTypes = [
        'changed',
        'general',
        'human',
        'left',
        'vacation'
      ];

      defaultTypes.forEach((t) => {
        this.selectedTypes[t] = true;
      });
      // Default is English only for new contacts
      this.selectedLanguages = [{
        id: 'en',
        itemName: 'English'
      }];
    }
    else {
      // Default is all languages
      this.selectedLanguages = JSON.parse(JSON.stringify(this.languageList));
    }

    let defaultFields: any[] =
      this.constants.coreFields.map((cf) => {
        return cf.partner == 'pardot' ? { id: cf.id, name: cf.name, description: cf.description, siftrock_description: cf.siftrockDescription, value: cf.value, core: true } : null
      }).filter((cf) => { return cf });

    if (dataType=='recipient') {
      // remove email address for recipient (existing contact) workflows
      defaultFields = defaultFields.filter((df) => { return df.name != 'email'});
    }

    this.workflow = {
      _id: 'new',
      type: 'integration',
      partner: this.partner.toLowerCase(),
      trigger: {
          type: defaultTypes,
          data: dataType,
          confidence: [1,2],
          language: ['all'],
          recipient: false
      },
      action: {
          list: {},
          fields: defaultFields,
          recipient_fields: [],
          static_fields: [],
          include_filters: [],
          exclude_filters: []
      },
      enabled: false,
      deleted: false
    };

    this.updateSession(dataType);
  }

  private updateSession(dataType) {
    // when a user creates a new workflow, re-set their session to whatever type they are creating
    // so when navigating back to workflows the created workflow will be on the screen
    this.sessionService.getSession().subscribe(
      (result: any) => {
        if (result.workflow_filters) {
          result.workflow_filters.type = 'all';
          result.workflow_filters.wf_type = dataType;
          result.workflow_filters.last_changed = new Date();
          this.sessionService.saveSession(result).subscribe(
            (result: any) => {
              // Nothing
            },
            err => {
              this.errorService.log(err, 'wf-detail.component', 'saveSession');
            }
          )
        }
      },
      error => {

      }
    )
  }
}



// WEBPACK FOOTER //
// ./client/app/config/workflows/wf-detail-pardot/wf-detail-pardot.component.ts